import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent, MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { debounceTime, merge, of, startWith, Subject, takeUntil } from 'rxjs';
import axios, { AxiosError } from 'axios';
import {
  AdditionalAction,
  CasesActionsMenuComponent,
} from '../../../shared/payee-cases/cases-actions-menu/cases-actions-menu.component';
import {
  RunTransitionWithDialog,
  WorkflowService,
} from '../../../shared/services/workflow/workflow.service';
import { environment } from 'src/environments/environment';
import { SendCustomMessageDialogComponent } from '../cases/send-custom-message-dialog/send-custom-message-dialog.component';
import { AdminCaseTableItem } from '../cases/cases.component';
import {
  PayeeCaseMokkRow,
  PayeeCaseMokkTransitionLogRow,
} from './payee-case-mokk.types';
import { UploadDebugMokkDialogComponent } from './upload-debug-mokk-dialog/upload-debug-mokk-dialog.component';
import { PayeeCaseMokkIdFilterComponent } from './payee-case-mokk-id-filter/payee-case-mokk-id-filter.component';
import { PayeeCaseMokkLabelFilterComponent } from './payee-case-mokk-label-filter/payee-case-mokk-label-filter.component';
import { PayeeCaseMokkPayeeCaseReferenceIdFilterComponent } from './payee-case-mokk-payee-case-reference-id-filter/payee-case-mokk-payee-case-reference-id-filter.component';
import { PayeeCaseMokkDebtorNameFilterComponent } from './payee-case-mokk-debtor-name-filter/payee-case-mokk-debtor-name-filter.component';
import { PayeeCaseMokkWithMediaFilterComponent } from './payee-case-mokk-with-media-filter/payee-case-mokk-with-media-filter.component';
import { PayeeCaseMokkWithoutTransitionLogFilterComponent } from './payee-case-mokk-without-transition-log-filter/payee-case-mokk-without-transition-log-filter.component';
import { PayeeCaseMokkShowHiddenFilterComponent } from './payee-case-mokk-show-hidden-filter/payee-case-mokk-show-hidden-filter.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { PayeeIconComponent } from '../../../shared/payee-icon/payee-icon.component';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { PayeeIconButtonComponent } from '../../../shared/payee-buttons/payee-icon-button/payee-icon-button.component';
import { PayeeButtonComponent } from '../../../shared/payee-buttons/payee-button/payee-button.component';
import { CaseEventsComponent } from '../../../shared/payee-cases/case-events/case-events.component';
import { CaseDetailsComponent } from '../../../shared/payee-cases/case-details/case-details.component';
import { DatePipe, KeyValuePipe } from '@angular/common';

@Component({
  selector: 'app-payee-case-mokk',
  templateUrl: './payee-case-mokk.component.html',
  styleUrls: ['./payee-case-mokk.component.scss'],
  imports: [
    PayeeCaseMokkIdFilterComponent,
    PayeeCaseMokkLabelFilterComponent,
    PayeeCaseMokkPayeeCaseReferenceIdFilterComponent,
    PayeeCaseMokkDebtorNameFilterComponent,
    PayeeCaseMokkWithMediaFilterComponent,
    PayeeCaseMokkWithoutTransitionLogFilterComponent,
    PayeeCaseMokkShowHiddenFilterComponent,
    MatProgressSpinner,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    PayeeIconComponent,
    MatIconButton,
    MatTooltip,
    MatIcon,
    PayeeIconButtonComponent,
    PayeeButtonComponent,
    CasesActionsMenuComponent,
    CaseEventsComponent,
    CaseDetailsComponent,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatPaginator,
    DatePipe,
    KeyValuePipe,
    TranslateModule,
  ],
})
export class PayeeCaseMokkComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  private workflowService = inject(WorkflowService);

  readonly isProduction = environment.production;

  private readonly destroy = new Subject<void>();
  private loadMokks = new Subject<void>();
  private mokkFetchGeneration = 0;
  private lockExpandedElement = false;

  expandedElement: PayeeCaseMokkRow | null = null;

  readonly displayedColumns: string[] = [
    'event_at',
    'mokk_id',
    'mokk_label',
    'payee_case_reference_id',
    'debtor',
    'state',
    'last_transition',
    'first_file',
    'actions',
  ];

  includeWithoutMediaControl = new FormControl<boolean>(false, {
    nonNullable: true,
  });
  includeWithTransitionsControl = new FormControl<boolean>(false, {
    nonNullable: true,
  });
  showHiddenControl = new FormControl<boolean>(false, { nonNullable: true });
  mokkIdFilterControl = new FormControl<string>('', { nonNullable: true });
  mokkLabelFilterControl = new FormControl<string>('', { nonNullable: true });
  payeeCaseReferenceIdFilterControl = new FormControl<string>('', {
    nonNullable: true,
  });
  debtorNameFilterControl = new FormControl<string>('', { nonNullable: true });

  dataSource = new MatTableDataSource<PayeeCaseMokkRow>([]);
  readonly loading = signal(false);

  total = 0;
  pageSize = 25;
  pageIndex = 0;

  readonly additionalActions: AdditionalAction[] = [
    {
      label: of<string>('Ügyfélszolgálati kérés'),
      onClick: (element) =>
        this.sendCustomMessage(element as AdminCaseTableItem),
      show: () => true,
    },
  ];

  ngOnInit(): void {
    this.setFiltersFromRoute();

    this.loadMokks
      .pipe(takeUntil(this.destroy), startWith(null), debounceTime(100))
      .subscribe(async () => await this.load());

    this.workflowService.afterWorkflow
      .pipe(takeUntil(this.destroy))
      .subscribe(() => this.loadMokks.next());

    merge(
      this.mokkIdFilterControl.valueChanges,
      this.mokkLabelFilterControl.valueChanges,
      this.payeeCaseReferenceIdFilterControl.valueChanges,
      this.debtorNameFilterControl.valueChanges,
      this.includeWithoutMediaControl.valueChanges,
      this.includeWithTransitionsControl.valueChanges,
      this.showHiddenControl.valueChanges,
    )
      .pipe(takeUntil(this.destroy), debounceTime(400))
      .subscribe(() => {
        this.pageIndex = 0;
        this.setFilterQueryParams();
        this.loadMokks.next();
      });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  async onPage(event: PageEvent): Promise<void> {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.setFilterQueryParams();
    this.loadMokks.next();
  }

  lastTransitionLog(
    row: PayeeCaseMokkRow,
  ): PayeeCaseMokkTransitionLogRow | null {
    return row.transition_logs[0] ?? null;
  }

  updateExpandedElement(row: PayeeCaseMokkRow): void {
    if (this.lockExpandedElement) {
      return;
    }
    if (this.expandedElement === row) {
      this.expandedElement = null;
    } else {
      this.expandedElement = row;
    }
  }

  onActionsMenuOpen(): void {
    this.lockExpandedElement = true;
  }

  onActionsMenuClosed(): void {
    this.lockExpandedElement = false;
  }

  hasMokkEvent(row: PayeeCaseMokkRow): boolean {
    return (row.transition_logs?.length ?? 0) > 0;
  }

  getTransitionMedia(
    row: PayeeCaseMokkRow,
  ): RunTransitionWithDialog['media'] {
    return row.mokk_media.map((file) => ({
      file_name: file.file_name,
      storage_url: file.url,
      uuid: file.uuid,
    }));
  }

  async copyCaseIdToClipboard(caseId: string, event: Event): Promise<void> {
    event.stopPropagation();

    try {
      await navigator.clipboard.writeText(caseId);

      this.snackBar.open(
        this.translate.instant('CASES_TABLE.COPY_SUCCESS'),
        this.translate.instant('OK'),
        { duration: 2000 },
      );
    } catch (error) {
      console.error('Failed to copy to clipboard', error);
    }
  }

  async setMokkHidden(
    row: PayeeCaseMokkRow,
    hidden: boolean,
    event: MouseEvent,
  ): Promise<void> {
    event.stopPropagation();

    try {
      await axios.patch(
        `${environment.baseUrl}/api/payee-case-mokk/${row.id}/hidden`,
        { hidden },
      );
      this.loadMokks.next();
      this.snackBar.open(
        hidden
          ? this.translate.instant('PAYEE_CASE_MOKK_TABLE.HIDE_SUCCESS')
          : this.translate.instant('PAYEE_CASE_MOKK_TABLE.UNHIDE_SUCCESS'),
        this.translate.instant('OK'),
        { duration: 3000 },
      );
    } catch (e) {
      let message = this.translate.instant('PAYEE_CASE_MOKK_TABLE.HIDE_ERROR');
      if (e instanceof AxiosError && e.response?.data?.message) {
        message = e.response.data.message;
      }
      console.error('Error while updating MOKK hidden state', e);
      this.snackBar.open(message, 'OK', { duration: 10_000 });
    }
  }

  async copyMokkIdToClipboard(mokkId: string, event: Event): Promise<void> {
    event.stopPropagation();

    try {
      await navigator.clipboard.writeText(mokkId);

      this.snackBar.open(
        this.translate.instant('PAYEE_CASE_MOKK_TABLE.COPY_MOKK_ID_SUCCESS'),
        this.translate.instant('OK'),
        { duration: 2000 },
      );
    } catch (error) {
      console.error('Failed to copy MOKK id to clipboard', error);
    }
  }

  private sendCustomMessage(element: AdminCaseTableItem): void {
    this.dialog.open(SendCustomMessageDialogComponent, {
      data: element,
      autoFocus: false,
    });
  }

  openUploadDebugMokkDialog(): void {
    const dialogRef = this.dialog.open(UploadDebugMokkDialogComponent, {
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe(uploaded => {
      if (uploaded) {
        this.loadMokks.next();
        this.snackBar.open(
          this.translate.instant('PAYEE_CASE_MOKK_TABLE.DEBUG_UPLOAD.SUCCESS'),
          this.translate.instant('OK'),
          { duration: 3000 },
        );
      }
    });
  }

  private async load(): Promise<void> {
    const generation = ++this.mokkFetchGeneration;

    try {
      this.loading.set(true);

      const params = this.getRequestParams();
      const res = await axios.get(
        `${environment.baseUrl}/api/payee-case-mokk`,
        { params },
      );

      if (generation !== this.mokkFetchGeneration) {
        return;
      }

      this.dataSource.data = res.data.data;
      this.total = res.data.pagination.total;
      this.expandedElement = null;
    } catch (e) {
      if (generation !== this.mokkFetchGeneration) {
        return;
      }

      let message = 'Nem sikerült betölteni a MOKK listát';
      if (e instanceof AxiosError && e.response?.data?.message) {
        message = e.response.data.message;
      }

      console.error('Error while loading mokks', e);
      this.snackBar.open(message, 'OK', { duration: 10_000 });
    } finally {
      if (generation === this.mokkFetchGeneration) {
        this.loading.set(false);
      }
    }
  }

  private setFilterQueryParams(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.getFilters(),
      queryParamsHandling: 'merge',
    });
  }

  private getFilters(): Record<string, string | number | null> {
    const debtorName = this.trimFilter(this.debtorNameFilterControl.value);

    return {
      include_without_media: this.includeWithoutMediaControl.value ? 1 : null,
      include_with_transitions: this.includeWithTransitionsControl.value
        ? 1
        : null,
      show_hidden: this.showHiddenControl.value ? 1 : null,
      mokk_id: this.trimFilter(this.mokkIdFilterControl.value) ?? null,
      mokk_label: this.trimFilter(this.mokkLabelFilterControl.value) ?? null,
      payee_case_reference_id:
        this.trimFilter(this.payeeCaseReferenceIdFilterControl.value) ?? null,
      debtor_name:
        debtorName && debtorName.length >= 3 ? debtorName : null,
      page: this.pageIndex + 1,
      per_page: this.pageSize,
    };
  }

  private getRequestParams(): Record<string, string | number> {
    return Object.fromEntries(
      Object.entries(this.getFilters()).filter(
        ([, value]) => value !== null && value !== '',
      ),
    ) as Record<string, string | number>;
  }

  private setFiltersFromRoute(): void {
    const queryParams = this.route.snapshot.queryParams;

    const include_without_media = queryParams.include_without_media;
    if (include_without_media) {
      this.includeWithoutMediaControl.patchValue(true);
    }
    const include_with_transitions = queryParams.include_with_transitions;
    if (include_with_transitions) {
      this.includeWithTransitionsControl.patchValue(true);
    }
    const show_hidden = queryParams.show_hidden;
    if (show_hidden) {
      this.showHiddenControl.patchValue(true);
    }
    const mokk_id = queryParams.mokk_id;
    if (mokk_id) {
      this.mokkIdFilterControl.patchValue(mokk_id);
    }
    const mokk_label = queryParams.mokk_label;
    if (mokk_label) {
      this.mokkLabelFilterControl.patchValue(mokk_label);
    }
    const payee_case_reference_id = queryParams.payee_case_reference_id;
    if (payee_case_reference_id) {
      this.payeeCaseReferenceIdFilterControl.patchValue(
        payee_case_reference_id,
      );
    }
    const debtor_name = queryParams.debtor_name;
    if (debtor_name) {
      this.debtorNameFilterControl.patchValue(debtor_name);
    }

    const page = parseInt(queryParams.page, 10);
    if (!Number.isNaN(page)) {
      this.pageIndex = Math.max(page, 1) - 1;
    }

    const perPage = parseInt(queryParams.per_page, 10);
    if (!Number.isNaN(perPage)) {
      this.pageSize = Math.max(perPage, 1);
    }
  }

  private trimFilter(value: unknown): string | undefined {
    if (value == null || typeof value !== 'string') {
      return undefined;
    }
    const t = value.trim();
    return t.length ? t : undefined;
  }
}
